import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { DEFAULT_EARLIEST, DEFAULT_FUTURE_LATEST, DEFAULT_LATEST } from 'constants/dateLimits';
import { setToStartOfMonth } from 'helpers/date';
import {
  federalDebtTypes, nonTaxDebtActionOptions, taxDebtActionOptions
} from 'constants/enums/financialOptions';
import currencyModelValidator from 'forms/shared/validators/models/currency';
import { REQUIRED } from 'constants/errors';
import { hasYesOrNo } from 'models/validate';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { LocationModel } from 'forms/shared/types';
import { notPOBoxPattern } from 'constants/patterns';
import { FinancialFederalDebtItemModel } from '../types';

type FederalDebtActivity = 'FAILED_TO_FILE' | 'FAILED_TO_PAY' | 'OTHER';

const isNotFailedToFileActivity = (activityType: FederalDebtActivity | undefined): boolean => !!(activityType === 'FAILED_TO_PAY' || activityType === 'OTHER');

const isNotOtherActivity = (activityType: FederalDebtActivity | undefined): boolean => !!(activityType === 'FAILED_TO_PAY' || activityType === 'FAILED_TO_FILE');

const isNotFailedToPayActivity = (activityType: FederalDebtActivity | undefined): boolean => !!(activityType === 'OTHER' || activityType === 'FAILED_TO_FILE');

const includesNoneOrOther = (values: (string | undefined)[] | undefined): boolean => !!(values?.includes?.('Other') || values?.includes?.('None'));

const customPvqLocationModelValidator: ValidateJSModel<LocationModel> = {
  ...pvqLocationModelValidator,
  street: {
    presence: true,
    format: { pattern: notPOBoxPattern, flags: 'i', message: 'error.location.street.poBox.pattern' },
  },
  county: {},
  country: {},
};

export const pvqFinancialFederalDebtItemValidator = (activityType: FederalDebtActivity): ValidateJSModel<FinancialFederalDebtItemModel> => ({
  FederalDebtType: (activityType === 'OTHER' ? {
    hasValue: {
      validator: {
        inclusion: {
          within: federalDebtTypes,
          length: {
            minimum: 1,
          },
        },
      },
    },
  } : {}),

  FederalDebtTypeReason: (value, attributes) => {
    if (activityType === 'OTHER' && attributes?.FederalDebtType?.value?.includes('Other')) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Year: (value, attributes, attributeName, options) => {
    if (isNotOtherActivity(activityType)) {
      return {
        presence: true,
        date: {
          requireDay: false,
          requireMonth: false,
          requireYear: true,
          earliest: setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST),
          latest: DEFAULT_FUTURE_LATEST,
          tooEarly: 'error.date.min',
        },
      };
    }

    return {};
  },

  Reason: () => {
    if (isNotFailedToPayActivity(activityType)) {
      return { hasValue: true };
    }

    return {};
  },

  ReasonNoActions: (value, attributes) => {
    if (activityType === 'FAILED_TO_FILE' && attributes?.Intent?.value === 'No') {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Intent: (activityType === 'FAILED_TO_FILE' ? {
    hasValue: {
      validator: hasYesOrNo,
    },
  } : {}),

  Date: (value, attributes, attributeName, options) => ((activityType === 'FAILED_TO_FILE' && attributes?.Intent?.value === 'Yes') || activityType === 'OTHER' ? {
    presence: true,
    date: {
      requireDay: false,
      requireMonth: true,
      requireYear: true,
      earliest: setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST),
      latest: DEFAULT_FUTURE_LATEST,
      tooEarly: 'error.date.min',
    },
  } : {}),

  Amount: (value, attributes) => ((isNotFailedToFileActivity(activityType) && attributes?.AmountIDK?.applicable !== false) ? {
    model: {
      validator: currencyModelValidator,
    },
  } : {}),

  AmountIDK: {},

  AmountEstimated: {},

  JudgementAmount: (value, attributes) => {
    if (activityType === 'OTHER' && attributes?.HasJudgementOrLien?.value === 'Yes') {
      return {
        hasValue: true,
        model: {
          validator: currencyModelValidator,
        },
      };
    }

    return {};
  },

  ActionsTaken: (value, attributes) => {
    if (activityType === 'FAILED_TO_PAY') {
      return {
        array: {
          validator: {
            inclusion: taxDebtActionOptions,
          },
          length: {
            minimum: 1,
            message: REQUIRED,
          },
        },
      };
    }

    if (activityType === 'OTHER' && attributes?.HasJudgementOrLien?.value === 'Yes') {
      return {
        array: {
          validator: {
            inclusion: nonTaxDebtActionOptions,
          },
          length: {
            minimum: 1,
            message: REQUIRED,
          },
        },
      };
    }

    return {};
  },

  ActionsTakenReason: (value, attributes) => {
    if (isNotFailedToFileActivity(activityType) && includesNoneOrOther(attributes?.ActionsTaken?.values)) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Agency: () => {
    if (activityType === 'OTHER') {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  CourtName: (value, attributes) => {
    if (activityType === 'OTHER' && attributes?.HasJudgementOrLien?.value === 'Yes') {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  CourtLocation: (value, attributes) => {
    if (activityType === 'OTHER' && attributes?.HasJudgementOrLien?.value === 'Yes') {
      return {
        presence: true,
        location: {
          validator: customPvqLocationModelValidator,
          requireCity: true,
          requireState: true,
        },
      };
    }

    return {};
  },

  AccountNum: () => {
    if (activityType === 'OTHER') {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  PropertyType: (value, attributes) => {
    if (activityType === 'OTHER' && attributes?.PropertyTypeNotApplicable?.applicable !== false) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  PropertyTypeNotApplicable: {},

  HasJudgementOrLien: activityType === 'OTHER' ? {
    hasValue: { validator: hasYesOrNo },
  } : {},

  JudgementOrLien: (value, attributes) => {
    if (activityType === 'OTHER' && attributes?.HasJudgementOrLien?.value === 'Yes') {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: ['Judgement', 'Lien'],
            },
          },
        },
      };
    }

    return {};
  },
});
